/**
 * CookieConsent - Cookie consent banner.
 *
 * Renders only while the visitor has not yet made a choice (getConsent()
 * returns null), so returning visitors never see it flash. Offers a real
 * refusal ("Essential only") alongside "Accept all" — dismissing the
 * banner is not an option, by design: nothing gated should load without
 * an affirmative choice either way, and closing without choosing would
 * leave that choice undefined.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { setConsent } from '../lib/consent';

const CookieConsent: React.FC<{ initiallyVisible: boolean }> = ({ initiallyVisible }) => {
  const [visible, setVisible] = useState(initiallyVisible);

  if (!visible) return null;

  const choose = (choice: 'accepted' | 'essential') => {
    setConsent(choice);
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 'var(--z-toast)' as unknown as number,
        backgroundColor: 'var(--color-surface)',
        borderTop: '1px solid var(--color-border)',
        boxShadow: 'var(--shadow-xl)',
        padding: 'var(--spacing-5) var(--spacing-4)',
      }}
    >
      <div
        className="layout-container"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--spacing-4)',
        }}
      >
        <p style={{ flex: '1 1 320px', margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)' }}>
          We use essential cookies to run this site, and — only if you accept — analytics cookies to
          understand how visitors use it. See our{' '}
          <Link to="/privacy" style={{ color: 'var(--color-primary)' }}>Privacy Policy</Link> for details
          and how to change your choice later.
        </p>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => choose('essential')}>
            Essential Only
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => choose('accepted')}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
